/**
 * Productions on the wire (PLAN §5.1, §4.1, D22): one run of a playlist (or of
 * a single rundown) in a studio.
 *
 *   - **The config snapshot (D22).** Creating a production copies the studio's
 *     config as it is at that moment (`config`, with `studioVersion`). Playout
 *     runs from the copy for the production's whole life; later studio edits
 *     apply to new productions only.
 *   - **Pinning (§4.1).** `packageHash` is the production package the process
 *     runs from, fixed at launch. Null until the node runtime pins it (phase 4).
 *   - **Redundancy (§8, D9).** `activeNode` is the node whose process is
 *     ACTIVE, `term` bumps on every promotion. Null / 0 until phase 4–5.
 *   - **Status.** `created` (not activated, or deactivated again), `active`
 *     (its playlist is activated for playout), `ended` (terminal: no more
 *     commands). `version` is the record's own optimistic-concurrency counter.
 *
 * A production lives in the studio it was created in, so its resource scope
 * for a permission check (D14) is that studio's (`studioId`, `siteId`).
 */
import { ulidSchema } from '@acp/domain/primitives.js';
import { z } from 'zod';
import { pageLimitSchema, pageTokenSchema, PAGE_LIMIT_DESCRIPTION, PAGE_TOKEN_DESCRIPTION } from './rows.js';
import { siteIdSchema, studioConfigSchema, studioIdSchema } from './studios.js';

export const PRODUCTION_STATUSES = ['created', 'active', 'ended'] as const;
export const productionStatusSchema = z.enum(PRODUCTION_STATUSES);
export type ProductionStatus = z.infer<typeof productionStatusSchema>;

const isoSchema = z.iso.datetime({ offset: true });
const userRefSchema = z.string().min(1).max(128);

/** A production package hash (D15): hex, as the package store names it. */
export const packageHashSchema = z.string().regex(/^[0-9a-f]{8,128}$/, 'a package hash is 8-128 lowercase hex characters');

/** A production without its config snapshot: what a listing shows. */
export const productionSummarySchema = z
  .object({
    productionId: ulidSchema,
    studioId: studioIdSchema,
    /** The studio's site when the production was created (the resource scope, D14). */
    siteId: siteIdSchema.nullable(),
    /** What it runs: exactly one of the two is set. */
    playlistId: ulidSchema.nullable(),
    rundownId: ulidSchema.nullable(),
    /** Created for rehearsal: `activate` defaults to it, and nothing is aired for real (Sofie's rehearsal mode). */
    rehearsal: z.boolean(),
    status: productionStatusSchema,
    /** The studio version the snapshot was taken from. */
    studioVersion: z.number().int().positive(),
    /** The pinned production package (§4.1); null until phase 4 pins it. */
    packageHash: packageHashSchema.nullable(),
    /** The node whose production process is ACTIVE (§8); null until one runs it. */
    activeNode: z.string().min(1).max(128).nullable(),
    /** The failover term (D9): bumped on every promotion. */
    term: z.number().int().nonnegative(),
    /** 1 on create, +1 per write of this record. */
    version: z.number().int().positive(),
    createdAt: isoSchema,
    createdBy: userRefSchema,
    endedAt: isoSchema.nullable(),
    endedBy: userRefSchema.nullable(),
    endReason: z.string().max(500).nullable(),
  })
  .strict();
export type ProductionSummary = z.infer<typeof productionSummarySchema>;

/** A production with the config it runs from (D22's snapshot). */
export const productionSchema = productionSummarySchema.extend({ config: studioConfigSchema }).strict();
export type Production = z.infer<typeof productionSchema>;

export const createProductionRequestSchema = z
  .object({
    /** Client-chosen ULID: a retried create returns the production as it is. */
    productionId: ulidSchema.optional(),
    studioId: studioIdSchema,
    /** Run a playlist… */
    playlistId: ulidSchema.nullable(),
    /** …or a single rundown. Exactly one of the two. */
    rundownId: ulidSchema.nullable(),
    rehearsal: z.boolean(),
  })
  .strict()
  .refine((body) => (body.playlistId === null) !== (body.rundownId === null), {
    message: 'set exactly one of playlistId and rundownId',
    path: ['playlistId'],
  });
export type CreateProductionRequest = z.infer<typeof createProductionRequestSchema>;

export const endProductionRequestSchema = z
  .object({
    /** Why, for the as-run log; null for none. */
    reason: z.string().max(500).nullable(),
  })
  .strict();
export type EndProductionRequest = z.infer<typeof endProductionRequestSchema>;

export const productionPathSchema = z.object({ productionId: ulidSchema.describe('The production’s id (a ULID).') }).strict();

export const listProductionsQuerySchema = z
  .object({
    studioId: studioIdSchema.optional().describe('Only the productions of this studio.'),
    status: productionStatusSchema.optional().describe('Only productions in this status.'),
    pageToken: pageTokenSchema.optional().describe(PAGE_TOKEN_DESCRIPTION),
    limit: pageLimitSchema.optional().describe(PAGE_LIMIT_DESCRIPTION),
  })
  .strict();

export const productionResponseSchema = z.object({ production: productionSchema }).strict();
export const listProductionsResponseSchema = z
  .object({
    /** Newest first. */
    productions: z.array(productionSummarySchema),
    nextPageToken: pageTokenSchema.nullable(),
  })
  .strict();
